import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { ClipboardText, GraduationCap, ShieldCheck, Users } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

const HERO = "/brand-daily-care.webp";

const FEATURES = [
  { icon: Users, text: "Clients, care plans & visit documentation" },
  { icon: GraduationCap, text: "Hiring, orientation & training" },
  { icon: ShieldCheck, text: "Employee files & compliance status" },
  { icon: ClipboardText, text: "Notes, follow-ups & the office calendar" },
];

export default function LoginPage() {
  const { user, loading, login, verifyLoginCode, resendLoginCode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dest = location.state?.from || new URLSearchParams(location.search).get("from") || "/dashboard";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [resent, setResent] = useState("");

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/login";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  useEffect(() => {
    if (!loading && user && (user.role === "admin" || (user.scopes || []).length)) {
      const scopeHome = (user.scopes || []).includes("references") ? "/references" : (user.scopes || []).includes("careplans") ? "/clients" : null;
      navigate(user.role === "admin" ? dest : (scopeHome || dest), { replace: true });
    }
  }, [user, loading]); /* eslint-disable-next-line */

  if (!loading && user && (user.role === "admin" || (user.scopes || []).length)) {
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(email, password);
      if (u && u.mfa_required) { setMfaToken(u.mfa_token); return; }
      const scopeHome = (u?.scopes || []).includes("references") ? "/references" : (u?.scopes || []).includes("careplans") ? "/clients" : null;
      navigate(u?.role !== "admin" && scopeHome ? scopeHome : dest);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await verifyLoginCode(mfaToken, code.trim());
      const scopeHome = (u?.scopes || []).includes("references") ? "/references" : (u?.scopes || []).includes("careplans") ? "/clients" : null;
      navigate(u?.role !== "admin" && scopeHome ? scopeHome : dest);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setError("");
    setResent("");
    try {
      const r = await resendLoginCode(mfaToken);
      setResent(`New code sent to ${r.sent_to || "your email"}.`);
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="flex items-center justify-center bg-background px-8 py-12">
        <div className="w-full max-w-md">
          <div className="mb-10 flex items-center gap-3">
            <img src="/shc-logo.png" alt="Silver Home Care" className="h-16 w-auto" data-testid="login-logo" />
            <div>
              <p className="font-heading text-xl font-semibold tracking-tight text-stone-900">Silver Home Care</p>
              <p className="text-xs text-stone-500">Administrative office</p>
            </div>
          </div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight text-stone-900">Office sign in</h1>
          <p className="mt-2 text-sm text-stone-500">
            For administrative staff. Caregivers — use the Caregiver portal link on the main site.
          </p>
          {user && user.role !== "admin" && (
            <p className="mt-3 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/10 p-3 text-xs text-[#8a5d14]" data-testid="login-switch-note">
              You're signed in as {user.name || user.email} (caregiver). Sign in here with an admin account to switch.
            </p>
          )}
          {mfaToken ? (
            <form onSubmit={submitCode} className="mt-8 space-y-5" data-testid="login-mfa-form">
              <div className="rounded-lg border border-[#6A3E97]/30 bg-[#6A3E97]/5 px-4 py-3 text-sm text-stone-700" data-testid="login-mfa-note">
                We emailed a 6-digit code to <b>{email}</b>. Enter it to finish signing in — it expires in 10 minutes.
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="code">Sign-in code</Label>
                <Input id="code" inputMode="numeric" autoComplete="one-time-code" required value={code}
                  data-testid="login-code-input" onChange={(e) => setCode(e.target.value)}
                  placeholder="6-digit code" className="h-11 border-stone-300 bg-white text-center text-lg tracking-[0.4em]" />
              </div>
              {error && (
                <div className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/10 px-4 py-2.5 text-sm text-[#C0392B]" data-testid="login-error">
                  {error}
                </div>
              )}
              {resent && (
                <div className="rounded-lg border border-[#2E7D32]/30 bg-[#2E7D32]/10 px-4 py-2.5 text-sm text-[#2E7D32]" data-testid="login-resent">
                  {resent}
                </div>
              )}
              <Button type="submit" disabled={busy || code.trim().length !== 6} data-testid="login-verify-button"
                className="h-11 w-full bg-primary text-primary-foreground hover:bg-stone-900">
                {busy ? "Verifying..." : "Verify & sign in"}
              </Button>
              <div className="flex items-center justify-between text-xs">
                <button type="button" onClick={resend} className="text-primary hover:underline" data-testid="login-resend-button">Resend code</button>
                <button type="button" onClick={() => { setMfaToken(""); setCode(""); setError(""); setResent(""); }}
                  className="text-stone-400 hover:text-stone-600" data-testid="login-mfa-back">← Use a different account</button>
              </div>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" required value={email} data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)} placeholder="you@silverhomecare.com"
                className="h-11 border-stone-300 bg-white" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" required value={password} data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                className="h-11 border-stone-300 bg-white" />
            </div>
            {error && (
              <div className="rounded-lg border border-[#C0392B]/30 bg-[#C0392B]/10 px-4 py-2.5 text-sm text-[#C0392B]" data-testid="login-error">
                {error}
              </div>
            )}
            <Button type="submit" disabled={busy} data-testid="login-submit-button"
              className="h-11 w-full bg-primary text-primary-foreground hover:bg-stone-900">
              {busy ? "Signing in..." : "Sign in"}
            </Button>
          </form>
          )}
          {!mfaToken && (
            <>
              <div className="mt-6 flex items-center gap-3" aria-hidden="true">
                <div className="h-px flex-1 bg-stone-200" /><span className="text-xs text-stone-400">or</span><div className="h-px flex-1 bg-stone-200" />
              </div>
              <Button type="button" variant="outline" onClick={googleSignIn} data-testid="google-signin-button"
                className="mt-4 h-11 w-full border-stone-300 bg-white font-semibold text-stone-800 hover:bg-stone-50">
                <svg viewBox="0 0 24 24" className="mr-2 h-4 w-4" aria-hidden="true">
                  <path fill="#4285F4" d="M23.5 12.3c0-.9-.1-1.5-.3-2.2H12v4.1h6.5c-.1 1.1-.8 2.7-2.4 3.8l3.7 2.9c2.2-2 3.7-5 3.7-8.6z"/>
                  <path fill="#34A853" d="M12 24c3.2 0 6-1.1 7.9-2.9l-3.7-2.9c-1 .7-2.4 1.2-4.2 1.2-3.2 0-6-2.2-7-5.1l-3.9 3C3.1 21.3 7.2 24 12 24z"/>
                  <path fill="#FBBC05" d="M5 14.3c-.2-.7-.4-1.5-.4-2.3s.2-1.6.4-2.3l-3.9-3C.4 8.2 0 10 0 12s.4 3.8 1.1 5.3l3.9-3z"/>
                  <path fill="#EA4335" d="M12 4.7c1.8 0 3 .8 3.7 1.4l3.3-3.2C17.9 1.1 15.2 0 12 0 7.2 0 3.1 2.7 1.1 6.7l3.9 3c1-2.9 3.8-5 7-5z"/>
                </svg>
                Sign in with Google
              </Button>
            </>
          )}
          <p className="mt-4 text-center text-xs text-stone-400">
            <a href="/" className="transition-colors hover:text-stone-600" data-testid="login-back-site">← Back to the main site</a>
          </p>
          <p className="mt-6 text-center text-xs text-stone-400">
            Completing a training or screening? Use the link the office sent you — no sign-in needed.
          </p>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Care professional" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-white/85" />
        <div className="absolute bottom-0 left-0 p-12 text-stone-900">
          <p className="font-heading text-4xl font-semibold leading-tight tracking-tight">
            Improving lives,<br />one visit at a time.
          </p>
          <p className="mt-3 max-w-md text-sm text-stone-600">
            Everything you need to run Silver Home Care — in one place.
          </p>
          <ul className="mt-6 max-w-md space-y-3">
            {FEATURES.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-stone-700">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#6A3E97]/10">
                  <Icon size={17} weight="fill" className="text-[#6A3E97]" />
                </span>
                {text}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
